import React, { useState, useRef, useEffect, useCallback } from "react";
import { MAP, TILE, TILE_SIZE, MAP_W, MAP_H, PLAYER_START, BUILDINGS, NPCS, ZONES, DECORATIONS } from "../data/overworldData";
import { INTERIORS, INT_TILE } from "../data/interiorData";
import PharmacistAdvisorGame from "./PharmacistAdvisorGame";
import MedMatchGame from "./MedMatchGame";
import DementiaCaseGame from "./DementiaCaseGame";
import DementiaTeachingGame from "./DementiaTeachingGame";
import PainTeachingGame from "./PainTeachingGame";
import { NAUSEA_MODULE } from "../data/nauseaTeachingData";
import { CONSTIPATION_MODULE } from "../data/constipationTeachingData";
import { VACCINE_CHILDREN_1, VACCINE_CHILDREN_2, VACCINE_ADULTS } from "../data/vaccineTeachingData";
import { IMPETIGO_MODULE, HFMD_MODULE } from "../data/paediatricTeachingData";
import { NOROVIRUS_MODULE } from "../data/norovirusTeachingData";
import { enemies } from "../data/battleData";
import MedRPG from "./MedRPG";
import Pokedex from "./Pokedex";
import MedicineCardCollection from "./MedicineCardCollection";
import Achievements from "./Achievements";

const VIEWPORT_W = 832;
const VIEWPORT_H = 544;
const MOVE_SPEED = 3;

// ── helpers ────────────────────────────────────────────────────────────────────

function shadeColor(hex, amount) {
  const num = parseInt(hex.replace("#", ""), 16);
  const r = Math.min(255, Math.max(0, (num >> 16) + amount));
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0xff) + amount));
  const b = Math.min(255, Math.max(0, (num & 0xff) + amount));
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
}

function isWalkable(tx, ty) {
  if (tx < 0 || ty < 0 || tx >= MAP_W || ty >= MAP_H) return false;
  const tile = MAP[ty][tx];
  return tile !== TILE.WALL && tile !== TILE.TREE && tile !== TILE.DECOR;
}

function isInteriorWalkable(interior, tx, ty) {
  if (tx < 0 || ty < 0 || tx >= interior.w || ty >= interior.h) return false;
  const t = interior.map[ty][tx];
  return t !== INT_TILE.WALL && t !== INT_TILE.DECOR;
}

function drawInteriorTile(ctx, tile, sx, sy, wallColor, floorColor = "#ede9fe") {
  const S = TILE_SIZE;
  if (tile === INT_TILE.FLOOR || tile === INT_TILE.DECOR) {
    ctx.fillStyle = floorColor;
    ctx.fillRect(sx, sy, S, S);
    ctx.strokeStyle = "rgba(0,0,0,0.07)";
    ctx.lineWidth = 0.5;
    ctx.strokeRect(sx + 0.5, sy + 0.5, S - 1, S - 1);
  } else if (tile === INT_TILE.WALL) {
    ctx.fillStyle = wallColor;
    ctx.fillRect(sx, sy, S, S);
    // Shelf highlight & shadow
    ctx.fillStyle = "rgba(255,255,255,0.2)";
    ctx.fillRect(sx + 2, sy + 2, S - 4, 5);
    ctx.fillStyle = "rgba(0,0,0,0.15)";
    ctx.fillRect(sx, sy + S - 3, S, 3);
  } else if (tile === INT_TILE.DOOR) {
    ctx.fillStyle = "#fcd34d";
    ctx.fillRect(sx, sy, S, S);
    ctx.fillStyle = "#78350f";
    ctx.fillRect(sx + 7, sy + 4, 18, 28);
    ctx.fillStyle = "#fbbf24";
    ctx.fillRect(sx + 9, sy + 6, 14, 24);
    ctx.beginPath();
    ctx.arc(sx + 20, sy + 18, 2, 0, Math.PI * 2);
    ctx.fillStyle = "#78350f";
    ctx.fill();
  } else if (tile === INT_TILE.STAIRS_UP || tile === INT_TILE.STAIRS_DOWN) {
    const isUp = tile === INT_TILE.STAIRS_UP;
    ctx.fillStyle = "#d6bc8a";
    ctx.fillRect(sx, sy, S, S);
    ctx.fillStyle = "#b8955a";
    // Draw 4 step bands
    for (let i = 0; i < 4; i++) {
      const banded = isUp ? i : (3 - i);
      ctx.fillRect(sx + banded * 4, sy + 4 + i * 7, S - banded * 8, 5);
    }
    ctx.fillStyle = "#7c4a1e";
    ctx.font = `bold ${S * 0.38}px sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(isUp ? "▲" : "▼", sx + S / 2, sy + S / 2 + 1);
  }
}

// ── tile renderer ──────────────────────────────────────────────────────────────

function drawTile(ctx, tile, sx, sy, col, row, tileImages = {}) {
  const S = TILE_SIZE;
  switch (tile) {
    case TILE.GRASS: {
      const grassImg = tileImages["/environment/grass.png"];
      if (grassImg && grassImg.complete && grassImg.naturalWidth > 0) {
        ctx.drawImage(grassImg, sx, sy, S, S);
      } else {
        ctx.fillStyle = "#86efac";
        ctx.fillRect(sx, sy, S, S);
        if ((col * 3 + row * 7) % 11 === 0) {
          ctx.fillStyle = "#4ade80";
          ctx.fillRect(sx + 5, sy + 10, 2, 7);
          ctx.fillRect(sx + 20, sy + 5,  2, 7);
        }
      }
      break;
    }
    case TILE.PATH: {
      const pathImg = tileImages["/environment/path.png"];
      if (pathImg && pathImg.complete && pathImg.naturalWidth > 0) {
        ctx.drawImage(pathImg, sx, sy, S, S);
      } else {
        ctx.fillStyle = "#d1d5db";
        ctx.fillRect(sx, sy, S, S);
        ctx.fillStyle = "#bfc4cc";
        ctx.fillRect(sx, sy + S / 2 - 1, S, 1);
        ctx.fillRect(sx + S / 2 - 1, sy, 1, S);
      }
      break;
    }
    case TILE.ROAD: {
      ctx.fillStyle = "#374151";
      ctx.fillRect(sx, sy, S, S);
      // Vertical road centre line
      if (col === 13 || col === 33) {
        ctx.fillStyle = "#fbbf24";
        ctx.fillRect(sx + S / 2 - 1, sy, 2, S);
      }
      // Horizontal road centre line
      if (row === 14 || row === 30) {
        ctx.fillStyle = "#fbbf24";
        ctx.fillRect(sx, sy + S / 2 - 1, S, 2);
      }
      break;
    }
    case TILE.TREE: {
      const treeImg = tileImages["/environment/tree1.png"];
      if (treeImg && treeImg.complete && treeImg.naturalWidth > 0) {
        // Draw grass underneath, then tree on top slightly larger for presence
        const grassImg = tileImages["/environment/grass.png"];
        if (grassImg && grassImg.complete && grassImg.naturalWidth > 0) {
          ctx.drawImage(grassImg, sx, sy, S, S);
        } else {
          ctx.fillStyle = "#86efac";
          ctx.fillRect(sx, sy, S, S);
        }
        // Draw tree centered, slightly oversized so it feels tall
        const ts = S + 8;
        ctx.drawImage(treeImg, sx - 4, sy - 8, ts, ts);
      } else {
        ctx.fillStyle = "#86efac";
        ctx.fillRect(sx, sy, S, S);
        ctx.fillStyle = "#92400e";
        ctx.fillRect(sx + 12, sy + 20, 8, 12);
        ctx.beginPath();
        ctx.arc(sx + S / 2, sy + 14, 13, 0, Math.PI * 2);
        ctx.fillStyle = "#15803d";
        ctx.fill();
        ctx.beginPath();
        ctx.arc(sx + S / 2, sy + 10, 9, 0, Math.PI * 2);
        ctx.fillStyle = "#166534";
        ctx.fill();
      }
      break;
    }
    case TILE.DOOR: {
      ctx.fillStyle = "#fcd34d";
      ctx.fillRect(sx, sy, S, S);
      // Door frame
      ctx.fillStyle = "#78350f";
      ctx.fillRect(sx + 7, sy + 4, 18, 28);
      // Door panel
      ctx.fillStyle = "#fbbf24";
      ctx.fillRect(sx + 9, sy + 6, 14, 24);
      // Handle
      ctx.beginPath();
      ctx.arc(sx + 20, sy + 18, 2, 0, Math.PI * 2);
      ctx.fillStyle = "#78350f";
      ctx.fill();
      break;
    }
    case TILE.DECOR: {
      // Non-walkable decoration footprint — draw grass underneath
      const grassImg = tileImages["/environment/grass.png"];
      if (grassImg && grassImg.complete && grassImg.naturalWidth > 0) {
        ctx.drawImage(grassImg, sx, sy, S, S);
      } else {
        ctx.fillStyle = "#86efac";
        ctx.fillRect(sx, sy, S, S);
      }
      break;
    }
    default: {
      ctx.fillStyle = "#86efac";
      ctx.fillRect(sx, sy, S, S);
    }
  }
}

function drawBuilding(ctx, building, camX, camY, buildingImages = {}, tileImages = {}) {
  const zone = ZONES[building.zoneId];
  const bx = building.x * TILE_SIZE - camX;
  const by = building.y * TILE_SIZE - camY;
  const bw = building.w * TILE_SIZE;
  const bh = building.h * TILE_SIZE;
  const S  = TILE_SIZE;

  // ── Sprite image path ──────────────────────────────────────────────────────
  if (building.image) {
    const img = buildingImages[building.image];
    if (img && img.complete && img.naturalWidth > 0) {
      // Tile grass.png as base so transparent parts look natural
      const grassImg = tileImages["/environment/grass.png"];
      if (grassImg && grassImg.complete && grassImg.naturalWidth > 0) {
        for (let ty = 0; ty < bh; ty += S)
          for (let tx = 0; tx < bw; tx += S)
            ctx.drawImage(grassImg, bx + tx, by + ty, S, S);
      } else {
        ctx.fillStyle = "#86efac";
        ctx.fillRect(bx, by, bw, bh);
      }
      // Draw image — optionally scaled down and aligned
      const scale = building.imageScale ?? 1;
      const imgW = bw * scale;
      const imgH = bh * scale;
      const imgX = bx + (bw - imgW) / 2;
      // "bottom" aligns image base just above the door row; default centres vertically
      const imgY = building.imageAlign === "bottom"
        ? by + bh - S - imgH
        : by + (bh - imgH) / 2;
      ctx.drawImage(img, imgX, imgY, imgW, imgH);
      // Name label on top
      ctx.font = "bold 12px 'Segoe UI', Arial, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = "rgba(0,0,0,0.55)";
      ctx.fillText(`${building.emoji} ${building.name}`, bx + bw / 2 + 1, by + bh / 2 + 1);
      ctx.fillStyle = "#ffffff";
      ctx.fillText(`${building.emoji} ${building.name}`, bx + bw / 2, by + bh / 2);
      return;
    }
  }

  // Main wall
  ctx.fillStyle = zone.wallColor;
  ctx.fillRect(bx, by, bw, bh);

  // Roof band (top 2 tiles)
  ctx.fillStyle = shadeColor(zone.wallColor, -40);
  ctx.fillRect(bx, by, bw, S * 2);

  // Roof ridge highlight
  ctx.fillStyle = shadeColor(zone.wallColor, -20);
  ctx.fillRect(bx, by, bw, 4);

  // Windows
  for (let wr = 2; wr < building.h - 1; wr += 3) {
    for (let wc = 1; wc < building.w - 1; wc += 2) {
      const wx = bx + wc * S + 6;
      const wy = by + wr * S + 6;
      const ww = S - 12;
      const wh = S - 10;
      // Window surround
      ctx.fillStyle = shadeColor(zone.wallColor, -20);
      ctx.fillRect(wx - 2, wy - 2, ww + 4, wh + 4);
      // Glass
      ctx.fillStyle = "#bfdbfe";
      ctx.fillRect(wx, wy, ww, wh);
      // Reflection
      ctx.fillStyle = "rgba(255,255,255,0.4)";
      ctx.fillRect(wx + 1, wy + 1, 4, wh - 2);
    }
  }

  // Building name label
  const labelY = by + bh / 2;
  const labelX = bx + bw / 2;
  ctx.font = "bold 11px 'Segoe UI', Arial, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  // Shadow
  ctx.fillStyle = "rgba(0,0,0,0.6)";
  ctx.fillText(`${building.emoji} ${building.name}`, labelX + 1, labelY + 1);
  // Text
  ctx.fillStyle = "#ffffff";
  ctx.fillText(`${building.emoji} ${building.name}`, labelX, labelY);
}

function drawPlayer(ctx, px, py, dir, animFrame, sprites) {
  const S  = TILE_SIZE;
  const cx = px + S / 2;
  const cy = py + S / 2;

  // Shadow
  ctx.beginPath();
  ctx.ellipse(cx, cy + 14, 9, 4, 0, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(0,0,0,0.25)";
  ctx.fill();

  if (sprites.loaded) {
    const frame = sprites.frames[animFrame % 4];
    if (dir === "left") {
      // Mirror horizontally for left-facing
      ctx.save();
      ctx.translate(px + S, py);
      ctx.scale(-1, 1);
      ctx.drawImage(frame, 0, 0, S, S);
      ctx.restore();
    } else {
      ctx.drawImage(frame, px, py, S, S);
    }
  } else {
    // Fallback circle while images load
    ctx.beginPath();
    ctx.arc(cx, cy - 1, 11, 0, Math.PI * 2);
    ctx.fillStyle = "#ffffff";
    ctx.fill();
    ctx.strokeStyle = "#3b82f6";
    ctx.lineWidth = 2.5;
    ctx.stroke();
    ctx.fillStyle = "#ef4444";
    ctx.fillRect(cx - 2, cy - 7, 4, 12);
    ctx.fillRect(cx - 6, cy - 3, 12, 4);
  }
}

function drawNPC(ctx, npc, sx, sy, npcImages = {}) {
  const S  = TILE_SIZE;
  const cx = sx + S / 2;
  const cy = sy + S / 2;

  // Shadow
  ctx.beginPath();
  ctx.ellipse(cx, cy + 14, 9, 4, 0, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(0,0,0,0.22)";
  ctx.fill();

  const img = npc.sprite ? npcImages[npc.sprite] : null;
  if (img && img.complete && img.naturalWidth > 0) {
    if (npc.sprite === "/blobavatar/blob.doctor.whitecoat.png") {
      const scale = 0.8;
      const w = S * scale, h = S * scale;
      ctx.drawImage(img, sx + (S - w) / 2, sy + (S - h) / 2, w, h);
    } else {
      ctx.drawImage(img, sx, sy, S, S);
    }
  } else {
    // Fallback circle
    ctx.beginPath();
    ctx.arc(cx, cy - 1, 10, 0, Math.PI * 2);
    ctx.fillStyle = npc.color;
    ctx.fill();
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.font = "bold 10px sans-serif";
    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("!", cx, cy - 1);
  }

  // Chat bubble above character
  ctx.font = "13px sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("💬", cx, sy - 6);
}

// ── component ──────────────────────────────────────────────────────────────────

const MedRPGOverworld = ({ onBack, userData, saveUserData }) => {
  const canvasRef = useRef(null);

  const [phase, setPhase]                   = useState("overworld");

  // ── Audio ─────────────────────────────────────────────────────────────────
  const overworldAudioRef = useRef(null);
  const battleAudioRef    = useRef(null);
  const studyAudioRef     = useRef(null);
  const [muted, setMuted] = useState(false);
  const mutedRef          = useRef(false);

  // Initialise audio elements once
  useEffect(() => {
    const ow = new Audio("/music/overworld.mp3");
    ow.loop   = true;
    ow.volume = 0.4;
    overworldAudioRef.current = ow;

    const bt = new Audio("/music/Battle.mp3");
    bt.loop   = true;
    bt.volume = 0.5;
    battleAudioRef.current = bt;

    const st = new Audio("/music/study.mp3");
    st.loop   = true;
    st.volume = 0.4;
    studyAudioRef.current = st;

    return () => { ow.pause(); bt.pause(); st.pause(); };
  }, []);

  // Switch tracks when phase changes
  useEffect(() => {
    const ow = overworldAudioRef.current;
    const bt = battleAudioRef.current;
    const st = studyAudioRef.current;
    if (!ow || !bt || !st) return;

    const allTracks = [ow, bt, st];
    const pauseAll = () => allTracks.forEach(t => t.pause());

    if (phase === "battle") {
      pauseAll();
      bt.currentTime = 0;
      if (!mutedRef.current) bt.play().catch(() => {});
    } else if (phase === "minigame") {
      pauseAll();
      if (!mutedRef.current) st.play().catch(() => {});
    } else {
      pauseAll();
      bt.currentTime = 0;
      st.currentTime = 0;
      if (!mutedRef.current) ow.play().catch(() => {});
    }
  }, [phase]);

  const toggleMute = () => {
    const next = !mutedRef.current;
    mutedRef.current = next;
    setMuted(next);
    const ow = overworldAudioRef.current;
    const bt = battleAudioRef.current;
    const st = studyAudioRef.current;
    if (!ow || !bt || !st) return;
    if (next) {
      ow.pause(); bt.pause(); st.pause();
    } else {
      if (phase === "battle") bt.play().catch(() => {});
      else if (phase === "minigame") st.play().catch(() => {});
      else ow.play().catch(() => {});
    }
  };

  const [activeZone, setActiveZone]         = useState(null);
  const [activeNPC, setActiveNPC]           = useState(null);
  const [npcPage, setNpcPage]               = useState(0);
  const [initialEnemy, setInitialEnemy]     = useState(null);
  const [displayedText, setDisplayedText]   = useState("");
  const [typewriterDone, setTypewriterDone] = useState(false);
  const typewriterRef     = useRef(null);
  const typewriterDoneRef = useRef(false);

  // Player movement state (in a ref so the game loop always sees current values)
  const playerRef = useRef({
    px: PLAYER_START.x * TILE_SIZE,
    py: PLAYER_START.y * TILE_SIZE,
    tx: PLAYER_START.x,
    ty: PLAYER_START.y,
    targetPx: PLAYER_START.x * TILE_SIZE,
    targetPy: PLAYER_START.y * TILE_SIZE,
    moving: false,
    dir: "down",
    stepCooldown: 0,
    animFrame: 0,
  });
  const spritesRef        = useRef({ loaded: false, frames: [] });
  const npcImagesRef      = useRef({});  // keyed by sprite path
  const buildingImagesRef = useRef({});  // keyed by image path
  const tileImagesRef     = useRef({});  // keyed by image path

  // Interior state
  const [activeInteriorId, setActiveInteriorId] = useState(null);
  const [activeGame, setActiveGame]             = useState(null);
  const interiorPlayerRef = useRef(null);
  // Tracks which phase to return to after NPC dialogue / shop overlay
  const returnPhaseRef = useRef("overworld");

  // Preload player walk sprites, NPC sprites, and building images
  useEffect(() => {
    // Player walk frames
    const frames = [1, 2, 3, 4].map(i => {
      const img = new Image();
      img.src = `/blobavatar/blob.walk${i}.png`;
      return img;
    });
    spritesRef.current.frames = frames;
    Promise.all(frames.map(f => new Promise(res => { f.onload = res; f.onerror = res; })))
      .then(() => { spritesRef.current.loaded = true; });

    // NPC sprites — overworld + all interior NPCs
    const interiorNpcSprites = Object.values(INTERIORS).flatMap(i => i.npcs.map(n => n.sprite));
    const uniqueNpcPaths = [...new Set([...NPCS.map(n => n.sprite), ...interiorNpcSprites].filter(Boolean))];
    uniqueNpcPaths.forEach(path => {
      const img = new Image();
      img.src = path;
      npcImagesRef.current[path] = img;
    });

    // Building images
    BUILDINGS.forEach(b => {
      if (b.image) {
        const img = new Image();
        img.src = b.image;
        buildingImagesRef.current[b.image] = img;
      }
    });

    // Tile images — overworld tiles + all interior decoration images
    const interiorDecPaths = Object.values(INTERIORS)
      .flatMap(i => (i.decorations || []).map(d => d.image));
    const envPaths = [
      "/environment/grass.png",
      "/environment/path.png",
      "/environment/tree1.png",
      ...DECORATIONS.map(d => d.image),
      ...interiorDecPaths,
    ];
    envPaths.forEach(path => {
      const img = new Image();
      img.src = path;
      tileImagesRef.current[path] = img;
    });
  }, []);

  // Lock body scroll on map phases; allow scroll in teaching/battle overlays
  const mapPhase = ["overworld", "interior", "npc"].includes(phase);
  useEffect(() => {
    if (mapPhase) window.scrollTo(0, 0);
    document.body.style.overflow           = mapPhase ? "hidden" : "";
    document.body.style.overscrollBehavior = mapPhase ? "none"   : "";
    return () => {
      document.body.style.overflow           = "";
      document.body.style.overscrollBehavior = "";
    };
  }, [mapPhase]);

  // Responsive scale — on mobile use a zoomed-in (smaller) virtual viewport so the player appears bigger
  const vpRef = useRef({ w: VIEWPORT_W, h: VIEWPORT_H });
  const [canvasSize, setCanvasSize] = useState({ w: VIEWPORT_W, h: VIEWPORT_H });
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const update = () => {
      const isMobile = window.innerWidth < 640;
      const vpW = isMobile ? Math.round(VIEWPORT_W / 2) : VIEWPORT_W;
      const vpH = isMobile ? Math.round(VIEWPORT_H / 2) : VIEWPORT_H;
      vpRef.current = { w: vpW, h: vpH };
      setCanvasSize({ w: vpW, h: vpH });
      const availW = window.innerWidth;
      const availH = window.innerHeight - 40 - (isMobile ? 160 : 0);
      setScale(Math.min(availW / vpW, availH / vpH, 1));
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const keysRef  = useRef({});
  const phaseRef = useRef("overworld");
  useEffect(() => { phaseRef.current = phase; }, [phase]);

  // NPC refs for keydown handler closures
  const activeNPCRef = useRef(null);
  const npcPageRef   = useRef(0);
  useEffect(() => { activeNPCRef.current = activeNPC; }, [activeNPC]);
  useEffect(() => { npcPageRef.current   = npcPage;   }, [npcPage]);

  // Typewriter effect — runs whenever the dialogue page changes
  useEffect(() => {
    if (phase !== "npc" || !activeNPC) return;
    const fullText = activeNPC.lines[npcPage] ?? "";
    setDisplayedText("");
    setTypewriterDone(false);
    typewriterDoneRef.current = false;
    let i = 0;
    clearInterval(typewriterRef.current);
    typewriterRef.current = setInterval(() => {
      i++;
      setDisplayedText(fullText.slice(0, i));
      if (i >= fullText.length) {
        clearInterval(typewriterRef.current);
        setTypewriterDone(true);
        typewriterDoneRef.current = true;
      }
    }, 22);
    return () => clearInterval(typewriterRef.current);
  }, [phase, activeNPC, npcPage]);

  const checkTriggers = useCallback((tx, ty) => {
    const tile = MAP[ty][tx];

    if (tile === TILE.DOOR) {
      const building = BUILDINGS.find(b => b.door.x === tx && b.door.y === ty);
      if (building) {
        const zone = ZONES[building.zoneId];

        // Enter interior if one exists for this zone
        if (INTERIORS[building.zoneId]) {
          const interior = INTERIORS[building.zoneId];
          interiorPlayerRef.current = {
            px: interior.playerEntry.x * TILE_SIZE,
            py: interior.playerEntry.y * TILE_SIZE,
            tx: interior.playerEntry.x,
            ty: interior.playerEntry.y,
            targetPx: interior.playerEntry.x * TILE_SIZE,
            targetPy: interior.playerEntry.y * TILE_SIZE,
            moving: false,
            dir: "up",
            stepCooldown: 0,
            animFrame: 0,
          };
          setActiveInteriorId(building.zoneId);
          setPhase("interior");
          return;
        }

        if (zone.isShop) {
          returnPhaseRef.current = "overworld";
          setActiveZone(zone);
          setPhase("shop");
          return;
        }
        const pool = enemies.filter(e => zone.enemies.includes(e.id));
        if (pool.length > 0) {
          const pick = pool[Math.floor(Math.random() * pool.length)];
          setInitialEnemy(pick);
          setActiveZone(zone);
          setPhase("zone-enter");
        }
      }
    }

    const npc = NPCS.find(n => n.x === tx && n.y === ty);
    if (npc) {
      returnPhaseRef.current = "overworld";
      setActiveNPC(npc);
      setNpcPage(0);
      setPhase("npc");
    }
  }, []);

  const checkInteriorTriggers = useCallback((tx, ty, interior) => {
    const tile = interior.map[ty][tx];

    if (tile === INT_TILE.DOOR) {
      interiorPlayerRef.current = null;
      setActiveInteriorId(null);
      returnPhaseRef.current = "overworld";
      setPhase("overworld");
      return;
    }

    if (tile === INT_TILE.STAIRS_UP || tile === INT_TILE.STAIRS_DOWN) {
      const link = (interior.stairsLinks || []).find(l => l.tx === tx && l.ty === ty);
      if (link && INTERIORS[link.targetId]) {
        const e = link.targetEntry;
        interiorPlayerRef.current = {
          px: e.x * TILE_SIZE, py: e.y * TILE_SIZE,
          tx: e.x, ty: e.y,
          targetPx: e.x * TILE_SIZE, targetPy: e.y * TILE_SIZE,
          moving: false, dir: "up", stepCooldown: 0, animFrame: 0,
        };
        setActiveInteriorId(link.targetId);
        return;
      }
    }

    const npc = interior.npcs.find(n => n.x === tx && n.y === ty);
    if (npc) {
      returnPhaseRef.current = "interior";
      setActiveNPC(npc);
      setNpcPage(0);
      setPhase("npc");
    }
  }, []);

  const checkExamineSpot = useCallback((tx, ty, interior) => {
    const spot = (interior.examineSpots || []).find(s => s.tx === tx && s.ty === ty);
    if (spot) {
      returnPhaseRef.current = "interior";
      setActiveNPC({ id: `examine-${tx}-${ty}`, color: "#6b7280", ...spot });
      setNpcPage(0);
      setPhase("npc");
    }
  }, []);

  // Keyboard input
  useEffect(() => {
    const handleDown = (e) => {
      keysRef.current[e.key] = true;
      if (["ArrowUp","ArrowDown","ArrowLeft","ArrowRight"," ","Enter","Escape"].includes(e.key)) {
        e.preventDefault();
      }

      const p = phaseRef.current;
      if (p === "npc") {
        if (e.key === " " || e.key === "Enter") {
          if (!typewriterDoneRef.current) {
            // Skip typewriter — reveal full text immediately
            clearInterval(typewriterRef.current);
            const npc = activeNPCRef.current;
            setDisplayedText(npc?.lines[npcPageRef.current] ?? "");
            setTypewriterDone(true);
            typewriterDoneRef.current = true;
          } else {
            // Advance to next line or close
            const npc = activeNPCRef.current;
            setNpcPage(prev => {
              if (prev + 1 >= (npc?.lines.length ?? 1)) {
                if (npc?.isShop) {
                  setPhase("shop");
                } else if (npc?.triggersGame) {
                  setActiveGame(npc.triggersGame);
                  setPhase("minigame");
                } else if (npc?.triggersBattle && npc?.zoneId) {
                  const zone = ZONES[npc.zoneId];
                  const pool = enemies.filter(e => zone.enemies.includes(e.id));
                  const pick = pool[Math.floor(Math.random() * pool.length)];
                  setInitialEnemy(pick);
                  setActiveZone(zone);
                  returnPhaseRef.current = "interior";
                  setPhase("zone-enter");
                } else {
                  setPhase(returnPhaseRef.current);
                }
                setActiveNPC(null);
                return 0;
              }
              return prev + 1;
            });
          }
        } else if (e.key === "Escape") {
          clearInterval(typewriterRef.current);
          setPhase(returnPhaseRef.current);
          setActiveNPC(null);
        }
      }
      if (p === "zone-enter") {
        if (e.key === " " || e.key === "Enter") setPhase("battle");
        if (e.key === "Escape") { setPhase(returnPhaseRef.current); setActiveZone(null); setInitialEnemy(null); }
      }
    };
    const handleUp = (e) => { delete keysRef.current[e.key]; };
    window.addEventListener("keydown", handleDown);
    window.addEventListener("keyup",   handleUp);
    return () => {
      window.removeEventListener("keydown", handleDown);
      window.removeEventListener("keyup",   handleUp);
    };
  }, []);

  // Game loop
  useEffect(() => {
    if (phase !== "overworld") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;

    const update = () => {
      const s = stateRef;
      if (s.moving) {
        const dx = s.targetPx - s.px;
        const dy = s.targetPy - s.py;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist <= MOVE_SPEED) {
          s.px = s.targetPx;
          s.py = s.targetPy;
          s.moving = false;
          s.animFrame = (s.animFrame + 1) % 4;
          checkTriggers(s.tx, s.ty);
        } else {
          s.px += (dx / dist) * MOVE_SPEED;
          s.py += (dy / dist) * MOVE_SPEED;
        }
      } else {
        s.stepCooldown = Math.max(0, s.stepCooldown - 1);
        if (s.stepCooldown === 0) {
          const k = keysRef.current;
          let nx = s.tx, ny = s.ty, dir = s.dir;
          if      (k["ArrowUp"]    || k["w"] || k["W"]) { ny--; dir = "up"; }
          else if (k["ArrowDown"]  || k["s"] || k["S"]) { ny++; dir = "down"; }
          else if (k["ArrowLeft"]  || k["a"] || k["A"]) { nx--; dir = "left"; }
          else if (k["ArrowRight"] || k["d"] || k["D"]) { nx++; dir = "right"; }
          s.dir = dir;
          if ((nx !== s.tx || ny !== s.ty) && isWalkable(nx, ny)) {
            s.tx = nx; s.ty = ny;
            s.targetPx = nx * TILE_SIZE;
            s.targetPy = ny * TILE_SIZE;
            s.moving = true;
            s.stepCooldown = 2;
          }
        }
      }
    };

    const draw = () => {
      const s = stateRef;
      const VW = vpRef.current.w;
      const VH = vpRef.current.h;
      const camX = s.px + TILE_SIZE / 2 - VW / 2;
      const camY = s.py + TILE_SIZE / 2 - VH / 2;

      // Background fill — use grass image if loaded, otherwise flat colour
      const grassBg = tileImagesRef.current["/environment/grass.png"];
      if (grassBg && grassBg.complete && grassBg.naturalWidth > 0) {
        for (let ry = 0; ry < VH; ry += TILE_SIZE)
          for (let rx = 0; rx < VW; rx += TILE_SIZE)
            ctx.drawImage(grassBg, rx, ry, TILE_SIZE, TILE_SIZE);
      } else {
        ctx.fillStyle = "#86efac";
        ctx.fillRect(0, 0, VW, VH);
      }

      const c0 = Math.floor(camX / TILE_SIZE) - 1;
      const r0 = Math.floor(camY / TILE_SIZE) - 1;
      const c1 = Math.ceil((camX + VW) / TILE_SIZE) + 1;
      const r1 = Math.ceil((camY + VH) / TILE_SIZE) + 1;

      // Ground layer (skip wall tiles — drawn by building renderer)
      for (let r = r0; r <= r1; r++) {
        for (let c = c0; c <= c1; c++) {
          const sx = c * TILE_SIZE - camX;
          const sy = r * TILE_SIZE - camY;
          let tile = TILE.GRASS;
          if (r >= 0 && r < MAP_H && c >= 0 && c < MAP_W) tile = MAP[r][c];
          if (tile !== TILE.WALL) drawTile(ctx, tile, sx, sy, c, r, tileImagesRef.current);
        }
      }

      // Buildings
      BUILDINGS.forEach(b => {
        const bRight  = (b.x + b.w) * TILE_SIZE - camX;
        const bBottom = (b.y + b.h) * TILE_SIZE - camY;
        const bLeft   = b.x * TILE_SIZE - camX;
        const bTop    = b.y * TILE_SIZE - camY;
        if (bRight > 0 && bLeft < VW && bBottom > 0 && bTop < VH) {
          drawBuilding(ctx, b, camX, camY, buildingImagesRef.current, tileImagesRef.current);
          // Re-draw door on top of building art
          drawTile(ctx, TILE.DOOR, b.door.x * TILE_SIZE - camX, b.door.y * TILE_SIZE - camY, b.door.x, b.door.y);
        }
      });

      // Decorations (fountain etc.) — drawn above ground, below NPCs
      DECORATIONS.forEach(dec => {
        const img = tileImagesRef.current[dec.image];
        if (!img || !img.complete || !img.naturalWidth) return;
        const dx = dec.tx * TILE_SIZE - camX;
        const dy = dec.ty * TILE_SIZE - camY;
        const dw = dec.tw * TILE_SIZE;
        const dh = dec.th * TILE_SIZE;
        if (dx + dw > 0 && dx < VW && dy + dh > 0 && dy < VH) {
          ctx.drawImage(img, dx, dy, dw, dh);
        }
      });

      // NPCs
      NPCS.forEach(npc => {
        const sx = npc.x * TILE_SIZE - camX;
        const sy = npc.y * TILE_SIZE - camY;
        if (sx > -TILE_SIZE && sx < VW + TILE_SIZE && sy > -TILE_SIZE && sy < VH + TILE_SIZE) {
          drawNPC(ctx, npc, sx, sy, npcImagesRef.current);
        }
      });

      // Player
      drawPlayer(ctx, s.px - camX, s.py - camY, s.dir, s.animFrame, spritesRef.current);
    };

    // Use the mutable ref directly in the loop
    const stateRef = playerRef.current;

    const loop = () => {
      update();
      draw();
      animId = requestAnimationFrame(loop);
    };
    animId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(animId);
  }, [phase, checkTriggers]);

  // ── Interior game loop ──────────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== "interior" || !activeInteriorId) return;
    const interior = INTERIORS[activeInteriorId];
    if (!interior || !interiorPlayerRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;

    const VW = vpRef.current.w;
    const VH = vpRef.current.h;
    const iState = interiorPlayerRef.current;

    const getOffsets = () => {
      const mapPixW = interior.w * TILE_SIZE;
      const mapPixH = interior.h * TILE_SIZE;
      const camX = mapPixW <= VW
        ? -(VW - mapPixW) / 2
        : Math.max(0, Math.min(iState.px + TILE_SIZE / 2 - VW / 2, mapPixW - VW));
      const camY = mapPixH <= VH
        ? -(VH - mapPixH) / 2
        : Math.max(0, Math.min(iState.py + TILE_SIZE / 2 - VH / 2, mapPixH - VH));
      return { offsetX: Math.floor(-camX), offsetY: Math.floor(-camY) };
    };

    const update = () => {
      if (iState.moving) {
        const dx = iState.targetPx - iState.px;
        const dy = iState.targetPy - iState.py;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist <= MOVE_SPEED) {
          iState.px = iState.targetPx;
          iState.py = iState.targetPy;
          iState.moving = false;
          iState.animFrame = (iState.animFrame + 1) % 4;
          checkInteriorTriggers(iState.tx, iState.ty, interior);
        } else {
          iState.px += (dx / dist) * MOVE_SPEED;
          iState.py += (dy / dist) * MOVE_SPEED;
        }
      } else {
        iState.stepCooldown = Math.max(0, iState.stepCooldown - 1);
        if (iState.stepCooldown === 0) {
          const k = keysRef.current;
          let nx = iState.tx, ny = iState.ty, dir = iState.dir;
          if      (k["ArrowUp"]    || k["w"] || k["W"]) { ny--; dir = "up"; }
          else if (k["ArrowDown"]  || k["s"] || k["S"]) { ny++; dir = "down"; }
          else if (k["ArrowLeft"]  || k["a"] || k["A"]) { nx--; dir = "left"; }
          else if (k["ArrowRight"] || k["d"] || k["D"]) { nx++; dir = "right"; }
          iState.dir = dir;
          if (nx !== iState.tx || ny !== iState.ty) {
            if (isInteriorWalkable(interior, nx, ny)) {
              iState.tx = nx; iState.ty = ny;
              iState.targetPx = nx * TILE_SIZE;
              iState.targetPy = ny * TILE_SIZE;
              iState.moving = true;
              iState.stepCooldown = 2;
            } else {
              checkExamineSpot(nx, ny, interior);
              iState.stepCooldown = 40; // prevent re-triggering while holding key
            }
          }
        }
      }
    };

    const draw = () => {
      const { offsetX, offsetY } = getOffsets();

      // Dark surround (interior is smaller than viewport)
      ctx.fillStyle = "#0f172a";
      ctx.fillRect(0, 0, VW, VH);

      // Tiles
      for (let r = 0; r < interior.h; r++) {
        for (let c = 0; c < interior.w; c++) {
          drawInteriorTile(
            ctx,
            interior.map[r][c],
            c * TILE_SIZE + offsetX,
            r * TILE_SIZE + offsetY,
            interior.wallColor,
            interior.floorColor
          );
        }
      }

      // Interior decorations (beds, furniture, etc.)
      (interior.decorations || []).forEach(dec => {
        const img = tileImagesRef.current[dec.image];
        if (!img || !img.complete || !img.naturalWidth) return;
        ctx.drawImage(
          img,
          dec.tx * TILE_SIZE + offsetX,
          dec.ty * TILE_SIZE + offsetY,
          dec.tw * TILE_SIZE,
          dec.th * TILE_SIZE
        );
      });

      // NPCs
      const completedMods = new Set(userData.completedModules || []);
      interior.npcs.forEach(npc => {
        const nx = npc.x * TILE_SIZE + offsetX;
        const ny = npc.y * TILE_SIZE + offsetY;
        drawNPC(ctx, npc, nx, ny, npcImagesRef.current);
        // ✅ tick above NPC if their module is completed
        if (npc.triggersGame && completedMods.has(npc.triggersGame)) {
          ctx.font = `${TILE_SIZE * 0.55}px serif`;
          ctx.textAlign = "center";
          ctx.textBaseline = "bottom";
          ctx.fillText("✅", nx + TILE_SIZE / 2, ny - 2);
        }
      });

      // Player
      drawPlayer(
        ctx,
        iState.px + offsetX,
        iState.py + offsetY,
        iState.dir,
        iState.animFrame,
        spritesRef.current
      );

      // Room name label
      ctx.font = "bold 13px 'Segoe UI', Arial, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.fillStyle = "rgba(0,0,0,0.5)";
      ctx.fillText(`💊 ${interior.name}`, VW / 2 + 1, 9);
      ctx.fillStyle = "#e9d5ff";
      ctx.fillText(`💊 ${interior.name}`, VW / 2, 8);
    };

    const loop = () => { update(); draw(); animId = requestAnimationFrame(loop); };
    animId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(animId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, activeInteriorId, checkInteriorTriggers, checkExamineSpot]);

  // ── Minigame phase ───────────────────────────────────────────────────────────

  // Helper: mark a module as completed and save
  const completeModule = (moduleId) => {
    const already = userData.completedModules || [];
    if (!already.includes(moduleId)) {
      const updated = [...already, moduleId];
      const newUserData = { ...userData, completedModules: updated };
      saveUserData(newUserData);
    }
    setPhase(returnPhaseRef.current);
    setActiveGame(null);
  };

  if (phase === "minigame" && activeGame === "pharmacist-advisor") {
    return (
      <PharmacistAdvisorGame
        onComplete={() => completeModule("pharmacist-advisor")}
        userData={userData}
        saveUserData={saveUserData}
      />
    );
  }

  if (phase === "minigame" && activeGame === "med-match-game") {
    return (
      <MedMatchGame
        onComplete={() => completeModule("med-match-game")}
        userData={userData}
        saveUserData={saveUserData}
      />
    );
  }

  if (phase === "minigame" && activeGame === "dementia-cases") {
    return (
      <DementiaCaseGame
        onComplete={() => completeModule("dementia-cases")}
        userData={userData}
        saveUserData={saveUserData}
      />
    );
  }

  if (phase === "minigame" && activeGame === "dementia-teaching-lewy") {
    return (
      <DementiaTeachingGame
        moduleId="lewy-body-parkinsons"
        onComplete={() => completeModule("dementia-teaching-lewy")}
        userData={userData}
        saveUserData={saveUserData}
      />
    );
  }

  if (phase === "minigame" && activeGame === "dementia-teaching-alzheimers") {
    return (
      <DementiaTeachingGame
        moduleId="alzheimers-ftd"
        onComplete={() => completeModule("dementia-teaching-alzheimers")}
        userData={userData}
        saveUserData={saveUserData}
      />
    );
  }

  if (phase === "minigame" && activeGame === "pain-teaching") {
    return (
      <PainTeachingGame
        onComplete={() => completeModule("pain-teaching")}
        userData={userData}
        saveUserData={saveUserData}
      />
    );
  }

  if (phase === "minigame" && activeGame === "nausea-teaching") {
    return (
      <PainTeachingGame
        module={NAUSEA_MODULE}
        onComplete={() => completeModule("nausea-teaching")}
        userData={userData}
        saveUserData={saveUserData}
      />
    );
  }

  if (phase === "minigame" && activeGame === "constipation-teaching") {
    return (
      <PainTeachingGame
        module={CONSTIPATION_MODULE}
        onComplete={() => completeModule("constipation-teaching")}
        userData={userData}
        saveUserData={saveUserData}
      />
    );
  }

  if (phase === "minigame" && activeGame === "dementia-teaching-vascular") {
    return (
      <DementiaTeachingGame
        moduleId="vascular-dementia"
        onComplete={() => completeModule("dementia-teaching-vascular")}
        userData={userData}
        saveUserData={saveUserData}
      />
    );
  }

  if (phase === "minigame" && activeGame === "impetigo-teaching") {
    return (
      <PainTeachingGame
        module={IMPETIGO_MODULE}
        onComplete={() => completeModule("impetigo-teaching")}
        userData={userData}
        saveUserData={saveUserData}
      />
    );
  }

  if (phase === "minigame" && activeGame === "hfmd-teaching") {
    return (
      <PainTeachingGame
        module={HFMD_MODULE}
        onComplete={() => completeModule("hfmd-teaching")}
        userData={userData}
        saveUserData={saveUserData}
      />
    );
  }

  if (phase === "minigame" && activeGame === "norovirus-teaching") {
    return (
      <PainTeachingGame
        module={NOROVIRUS_MODULE}
        onComplete={() => completeModule("norovirus-teaching")}
        userData={userData}
        saveUserData={saveUserData}
      />
    );
  }

  if (phase === "minigame" && activeGame === "vaccine-children-1") {
    return (
      <PainTeachingGame
        module={VACCINE_CHILDREN_1}
        onComplete={() => completeModule("vaccine-children-1")}
        userData={userData}
        saveUserData={saveUserData}
      />
    );
  }

  if (phase === "minigame" && activeGame === "vaccine-children-2") {
    return (
      <PainTeachingGame
        module={VACCINE_CHILDREN_2}
        onComplete={() => completeModule("vaccine-children-2")}
        userData={userData}
        saveUserData={saveUserData}
      />
    );
  }

  if (phase === "minigame" && activeGame === "vaccine-adults") {
    return (
      <PainTeachingGame
        module={VACCINE_ADULTS}
        onComplete={() => completeModule("vaccine-adults")}
        userData={userData}
        saveUserData={saveUserData}
      />
    );
  }

  // ── Battle phase: render MedRPG ─────────────────────────────────────────────
  if (phase === "battle" && activeZone && initialEnemy) {
    return (
      <MedRPG
        onBack={() => { setPhase(returnPhaseRef.current); setActiveZone(null); setInitialEnemy(null); }}
        userData={userData}
        saveUserData={saveUserData}
        initialEnemy={initialEnemy}
        onComplete={() => { setPhase(returnPhaseRef.current); setActiveZone(null); setInitialEnemy(null); }}
      />
    );
  }

  // ── Medicine Card Collection ─────────────────────────────────────────────────
  if (phase === "cards") {
    return (
      <MedicineCardCollection
        medicineCards={userData?.medicineCards || []}
        onClose={() => setPhase(returnPhaseRef.current)}
      />
    );
  }

  // ── Pokédex ──────────────────────────────────────────────────────────────────
  if (phase === "pokedex") {
    return (
      <Pokedex
        defeatedEnemies={userData?.defeatedEnemies || []}
        onClose={() => setPhase(returnPhaseRef.current)}
      />
    );
  }

  if (phase === "achievements") {
    return (
      <Achievements
        achievements={userData?.achievements || []}
        onClose={() => setPhase(returnPhaseRef.current)}
      />
    );
  }

  // ── Overworld UI ────────────────────────────────────────────────────────────
  return (
    <div
      className={`${mapPhase ? "h-screen overflow-hidden" : "min-h-screen overflow-auto"} bg-gray-950 flex flex-col items-center`}
      style={mapPhase ? { touchAction: "none", overscrollBehavior: "none" } : {}}
    >
      {/* Header */}
      <div className="w-full" style={{ maxWidth: canvasSize.w }}>
        <div className="flex items-center justify-between px-4 py-2 bg-gray-900 text-white">
          <button onClick={onBack} className="text-sm text-gray-300 hover:text-white flex items-center gap-1">
            ← Menu
          </button>
          <span className="font-bold text-sm tracking-wide">🗺️ MedQuest Town</span>
          <div className="flex gap-2">
            <button
              onClick={toggleMute}
              className="text-sm bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded-lg transition-colors"
              title={muted ? "Unmute music" : "Mute music"}
            >
              {muted ? "🔇" : "🔊"}
            </button>
            <button
              onClick={() => { returnPhaseRef.current = phase; setPhase("pokedex"); }}
              className="text-sm bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded-lg transition-colors"
            >
              🧫 Pathogens
            </button>
            <button
              onClick={() => { returnPhaseRef.current = phase; setPhase("cards"); }}
              className="text-sm bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded-lg transition-colors"
            >
              💊 Cards
            </button>
            <button
              onClick={() => { returnPhaseRef.current = phase; setPhase("achievements"); }}
              className="text-sm bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded-lg transition-colors"
            >
              🏅 Badges
            </button>
          </div>
        </div>
      </div>

      {/* Canvas + overlays — outer div sets the scaled footprint, inner div applies the transform */}
      <div style={{ width: canvasSize.w * scale, height: canvasSize.h * scale, flexShrink: 0 }}>
        <div className="relative" style={{ width: canvasSize.w, height: canvasSize.h, transform: `scale(${scale})`, transformOrigin: "top left" }}>
        <canvas
          ref={canvasRef}
          width={canvasSize.w}
          height={canvasSize.h}
          className="block"
          style={{ imageRendering: "pixelated" }}
        />

        {/* RPG Dialogue box — floats over bottom of canvas */}
        {phase === "npc" && activeNPC && (
          <div className="absolute bottom-5 left-5 right-5 z-20 select-none">
            {/* Name plate tab */}
            <div
              className="inline-flex items-center gap-2 px-4 py-1 rounded-t-xl ml-4 border-2 border-b-0 border-yellow-400"
              style={{ background: "#0f172a" }}
            >
              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: activeNPC.color }} />
              <span className="text-yellow-300 font-bold text-sm tracking-wide">{activeNPC.name}</span>
            </div>

            {/* Main dialogue box */}
            <div
              className="border-2 border-yellow-400 rounded-2xl rounded-tl-none p-4 shadow-2xl"
              style={{ background: "rgba(15,23,42,0.97)" }}
            >
              <div className="flex gap-4 items-start">
                {/* Portrait */}
                <div className="w-14 h-14 rounded-xl flex-shrink-0 border-2 border-white/20 overflow-hidden flex items-center justify-center"
                  style={{ backgroundColor: activeNPC.color }}>
                  {activeNPC.sprite
                    ? <img src={activeNPC.sprite} alt={activeNPC.name} className="w-full h-full object-contain" style={{ imageRendering: "pixelated" }} />
                    : <span className="text-xl font-extrabold text-white">{activeNPC.name[0]}</span>
                  }
                </div>

                {/* Text area */}
                <div className="flex-1 min-h-[56px] flex flex-col justify-between">
                  <p className="text-white text-sm leading-relaxed">
                    {displayedText}
                    {!typewriterDone && (
                      <span className="animate-pulse text-yellow-300">▌</span>
                    )}
                  </p>
                  {typewriterDone && (
                    <div className="text-right mt-2">
                      <span className="text-yellow-400 text-xs font-semibold animate-bounce inline-block">
                        {npcPage < activeNPC.lines.length - 1
                          ? "▼  next"
                          : "▼  close"}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        </div> {/* end transform wrapper */}
      </div>   {/* end scaled footprint */}

      {/* D-pad + action button (mobile) */}
      <div className="mt-16 flex items-center gap-8 sm:hidden pb-4">
        {/* D-pad */}
        <div className="flex flex-col items-center gap-2">
          <button
            onPointerDown={() => { keysRef.current["ArrowUp"] = true; }}
            onPointerUp={()   => { delete keysRef.current["ArrowUp"]; }}
            className="w-16 h-16 bg-gray-700 text-white rounded-xl text-2xl active:bg-gray-500 select-none shadow-md"
          >▲</button>
          <div className="flex gap-2">
            {[["ArrowLeft","◀"],["ArrowDown","▼"],["ArrowRight","▶"]].map(([key, label]) => (
              <button
                key={key}
                onPointerDown={() => { keysRef.current[key] = true; }}
                onPointerUp={()   => { delete keysRef.current[key]; }}
                className="w-16 h-16 bg-gray-700 text-white rounded-xl text-2xl active:bg-gray-500 select-none shadow-md"
              >{label}</button>
            ))}
          </div>
        </div>

        {/* Action button */}
        <button
          onPointerDown={() => window.dispatchEvent(new KeyboardEvent("keydown", { key: " ", bubbles: true }))}
          className="w-20 h-20 rounded-full text-white font-extrabold text-xl active:scale-95 select-none shadow-lg"
          style={{ background: "linear-gradient(135deg,#22c55e,#16a34a)" }}
        >A</button>
      </div>

      {/* Zone Enter overlay */}
      {phase === "zone-enter" && activeZone && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-sm w-full mx-4 text-center">
            <div className="text-5xl mb-3">{activeZone.emoji}</div>
            <h2 className="text-2xl font-extrabold text-gray-900 mb-2">{activeZone.name}</h2>
            <p className="text-gray-500 text-sm mb-4">{activeZone.description}</p>
            {initialEnemy && (
              <p className="text-xs text-gray-400 mb-6">
                A patient presents with <span className="font-semibold text-gray-600">{initialEnemy.disease}</span>
              </p>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => setPhase("battle")}
                className="flex-1 py-3 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold hover:from-green-400 hover:to-emerald-400 transition-all"
              >
                Enter →
              </button>
              <button
                onClick={() => { setPhase(returnPhaseRef.current); setActiveZone(null); setInitialEnemy(null); }}
                className="flex-1 py-3 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold transition-all"
              >
                Back
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-3">SPACE to enter · ESC to cancel</p>
          </div>
        </div>
      )}

      {/* Shop overlay (Pharmacy) */}
      {phase === "shop" && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-sm w-full mx-4 text-center">
            <div className="text-5xl mb-3">💊</div>
            <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Pharmacy</h2>
            <div className="flex justify-center gap-6 mb-4">
              <div className="bg-yellow-50 rounded-xl px-5 py-3 border border-yellow-200">
                <div className="text-xl font-bold text-yellow-600">{userData?.coins ?? 0}</div>
                <div className="text-xs text-yellow-700">Coins 🪙</div>
              </div>
              <div className="bg-purple-50 rounded-xl px-5 py-3 border border-purple-200">
                <div className="text-xl font-bold text-purple-600">{userData?.xp ?? 0}</div>
                <div className="text-xs text-purple-700">XP ⭐</div>
              </div>
            </div>
            <p className="text-gray-400 text-xs mb-6">
              🔒 Items & upgrades coming soon! Keep battling to earn coins and XP.
            </p>
            <button
              onClick={() => { setPhase(returnPhaseRef.current); setActiveZone(null); }}
              className="w-full py-3 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold"
            >
              Leave
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MedRPGOverworld;
